import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { selectedPaymentMethodState } from "src/components/features/cart/confirm/PaymentSelection";
import { useAuPayConfirm } from "src/components/features/cart/confirm/useAuPayConfirm";
import { useCallbackOrderSuccess } from "src/components/features/cart/confirm/useCallbackOrderSuccess";
import { useDDDSecureOrder } from "src/components/features/cart/confirm/useDDDSecureOrder";
import { useOrderPontaPoint } from "src/components/features/cart/confirm/useOrderPontaPoint";
import { SubstituteItemPairForApi } from "src/modules/api/Order/orderCartListForDelivery/types";
import { useOrderCartListForDeliveryTrigger } from "src/modules/api/Order/orderCartListForDelivery/useOrderCartListForDelivery";
import { useGetCartList } from "src/modules/api/Shop/getCartList/getCartList";
import { useGetUserDDDInfoTrigger } from "src/modules/api/User/getUserDDDInfo/getUserDDDInfo";
import { useCreditCardList } from "src/modules/domain/order/payment/creditCard/useCreditCartList";
import { PAYMENT_TYPE } from "src/modules/domain/order/payment/paymentType/paymentType";
import { RECEIPT_METHOD_TYPE } from "src/modules/domain/user/deliveryAddress/receiptMethodType";
import { useUserDeliveryAddressState } from "src/modules/domain/user/deliveryAddress/useUserDeliveryAddressState";
import { UserData } from "src/modules/domain/user/userData";
import { useUserDataState } from "src/modules/domain/user/userData/useUserDataState";
import { getLocalStorage, setLocalStorage } from "src/modules/localStorage";
import {
  mandatoryItemList,
  substituteItemList,
  substituteSuggestionItemFlag,
} from "./OrderDisclaimer/useRadioState";

export const useCartConfirm = () => {
  const router = useRouter();
  const { userDataState: userData } = useUserDataState();
  const { deliveryAddressState: deliveryAddress, isReady: isAddressReady } =
    useUserDeliveryAddressState();
  const { data: cartList } = useGetCartList(
    {
      version: 2,
      latitude: `${deliveryAddress?.latitude}`,
      longitude: `${deliveryAddress?.longitude}`,
    },
    isAddressReady
  );
  const pontaPointForUseResult = useMemo(() => {
    return getLocalStorage<number>("pontaPointForUse");
  }, []);
  const pontaPointForUse = useMemo(() => {
    return pontaPointForUseResult.isStored ? pontaPointForUseResult.value : 0;
  }, [pontaPointForUseResult]);
  const { creditCardListData, updateCreditCardList } = useCreditCardList();
  const {
    obtainablePontaPoint,
    obtainableSmartPassPontaPoint,
    isRegisteredAuId,
  } = useOrderPontaPoint(cartList);

  const { trigger: userDDDInfoTrigger } = useGetUserDDDInfoTrigger();
  const { trigger: orderCartListForDeliveryTrigger } =
    useOrderCartListForDeliveryTrigger();
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);

  const onOrderSuccess = useCallbackOrderSuccess();
  const { orderByDDDSecure } = useDDDSecureOrder();
  const { linkWithAuId } = useAuPayConfirm();

  const mandatoryList = useRecoilValue(mandatoryItemList);
  const resetMandatoryList = useResetRecoilState(mandatoryItemList);
  const substituteSuggestionFlag = useRecoilValue(substituteSuggestionItemFlag);
  const resetsubstituteSuggestionFlag = useResetRecoilState(
    substituteSuggestionItemFlag
  );
  const selectedPaymentMethod = useRecoilValue(selectedPaymentMethodState);

  // Set activeOrder in Local Storage
  const activeOrder = getLocalStorage<string>("activeOrder");
  const handleSetActiveOrder = useCallback(
    (orderId: string) => {
      const activeOrderLs: string[] = activeOrder.isStored
        ? JSON.parse(activeOrder.value)
        : [];

      activeOrderLs.push(orderId);
      setLocalStorage("activeOrder", JSON.stringify(activeOrderLs));
    },
    [activeOrder]
  );

  const substituteList = useRecoilValue(substituteItemList);
  const resetSubstituteList = useResetRecoilState(substituteItemList);

  const substituteListParams = useMemo(() => {
    let _data: SubstituteItemPairForApi[] = [];
    substituteList.substituteItemPairs.map((pair) => {
      _data = [
        ..._data,
        ...[
          {
            cart_item_id: pair.baseCartItemId,
            substitute_shop_item_id: pair.substituteItem.shop_item_id,
          },
        ],
      ];
    });
    return _data;
  }, [substituteList.substituteItemPairs]);
  const shopItemShortageParams = useMemo(() => {
    return substituteList.substituteItemPairs.length !== 0
      ? {
          substitute_item_list: substituteListParams,
          is_selected_substitute_item_suggestion: substituteSuggestionFlag,
        }
      : mandatoryList.length !== 0
      ? {
          must_have_cart_item_id_list: mandatoryList,
          is_selected_substitute_item_suggestion: substituteSuggestionFlag,
        }
      : {};
  }, [
    substituteList.substituteItemPairs.length,
    substituteListParams,
    substituteSuggestionFlag,
    mandatoryList,
  ]);
  const order = useCallback(
    async (isOutOfStockAllowed: boolean) => {
      try {
        setIsProcessingOrder(true);
        if (!userData || !cartList?.active_cart || !deliveryAddress) {
          return;
        }
        const cartData = cartList.active_cart;
        const receiptMethod = deliveryAddress?.receipt_method;

        const userDDDInfoData = await userDDDInfoTrigger({
          params: { cart_id: cartData.cart_id },
        });

        const isRequiredCardholderName =
          !userDDDInfoData?.is_registered_cardholder_name &&
          userDDDInfoData?.is_required_ddd_authorization;

        const currentCreditCard =
          creditCardListData?.credit_card_list[
            creditCardListData.select_card_index
          ];

        if (
          creditCardListData?.credit_card_list.length === 0 &&
          !PAYMENT_TYPE.AU
        ) {
          router.push("/mypage/payment");
          return;
        }
        if (!UserData.existsNickName(userData)) {
          router.push("/account/nickName");
          return;
        }
        if (!UserData.existsEmail(userData)) {
          router.push({ pathname: "/account/mail", query: { fromCart: true } });
          return;
        }
        if (!UserData.existsTel(userData)) {
          router.push("/account/tel");
          return;
        }

        if (selectedPaymentMethod === PAYMENT_TYPE.AU) {
          if (isRegisteredAuId === false) {
            router.push("/mypage/connectIds/auId/auIdAgreements");
            return;
          }
          return linkWithAuId(
            cartData,
            obtainablePontaPoint,
            obtainableSmartPassPontaPoint,
            isOutOfStockAllowed,
            deliveryAddress.user_delivery_address_id
          );
        }

        if (isRequiredCardholderName && currentCreditCard) {
          router.push({
            pathname: "/mypage/payment/update-credit-card",
            query: {
              q: currentCreditCard.card_id,
              type: "updateCreditCardConfirmPage",
            },
          });
          return;
        }
        if (
          receiptMethod === RECEIPT_METHOD_TYPE.PUT_AT_ENTRANCE &&
          (UserData.isPlacementNg(userData) || cartData.is_placement_ng)
        ) {
          return;
        }
        if (userDDDInfoData?.is_required_ddd_authorization) {
          return orderByDDDSecure(
            cartData,
            obtainablePontaPoint,
            obtainableSmartPassPontaPoint,
            isOutOfStockAllowed,
            onOrderSuccess
          );
        }
        const params = {
          params: {
            cart_id: cartData.cart_id,
            total_price: cartData.user_payment_price - pontaPointForUse,
            payment_type: PAYMENT_TYPE.CREDIT,
            used_reason: "",
            user_delivery_address_id: deliveryAddress.user_delivery_address_id,
            deluxe_request: "",
            is_allowed_out_of_stock: isOutOfStockAllowed,
            use_ponta_point: pontaPointForUse || 0,
            obtain_ponta_point: obtainablePontaPoint,
            apple_pay_token: "",
            obtain_smart_pass_ponta_point: obtainableSmartPassPontaPoint,
            is_selected_substitute_item_suggestion: substituteSuggestionFlag,
            ...shopItemShortageParams,
          },
        };
        const res = await orderCartListForDeliveryTrigger(params);
        if (res) {
          onOrderSuccess(res.order_no);
          resetMandatoryList();
          handleSetActiveOrder(res.order_no);
          setLocalStorage("pontaPointForUse", "0");
          resetSubstituteList();
          resetsubstituteSuggestionFlag();
        }
      } finally {
        setIsProcessingOrder(false);
        resetMandatoryList();
        resetSubstituteList();
        resetsubstituteSuggestionFlag();
      }
    },
    [
      userData,
      cartList?.active_cart,
      deliveryAddress,
      userDDDInfoTrigger,
      creditCardListData?.credit_card_list,
      creditCardListData?.select_card_index,
      selectedPaymentMethod,
      pontaPointForUse,
      obtainablePontaPoint,
      obtainableSmartPassPontaPoint,
      substituteSuggestionFlag,
      shopItemShortageParams,
      orderCartListForDeliveryTrigger,
      router,
      isRegisteredAuId,
      linkWithAuId,
      orderByDDDSecure,
      onOrderSuccess,
      resetMandatoryList,
      handleSetActiveOrder,
      resetSubstituteList,
      resetsubstituteSuggestionFlag,
    ]
  );

  const validateCardHolderName = useCallback(async () => {
    if (!userData || !cartList?.active_cart || !deliveryAddress) {
      return;
    }
    const cartData = cartList.active_cart;
    const userDDDInfoData = await userDDDInfoTrigger({
      params: { cart_id: cartData.cart_id },
    });
    return userDDDInfoData?.is_registered_cardholder_name;
  }, [cartList?.active_cart, deliveryAddress, userDDDInfoTrigger, userData]);

  return {
    cartData: cartList?.active_cart,
    creditCardListData,
    isProcessingOrder,
    updateCreditCardList,
    order,
    validateCardHolderName,
  };
};
